// src/auth/password-policy.ts
//
// The new-password strength rules, ported VERBATIM from Flutter
// `reset_password_new_password_bloc.dart` (EnabledButtonEvent) — the same rules
// also gate `create_password`. The submit button enables only when every rule
// passes AND password === confirmPassword.
//
// Flutter rule set:
//   valid = length >= 8
//         && matches(pw, '[0-9]')            // a digit
//         && matches(pw, '[a-z]')            // a lowercase letter
//         && matches(pw, '[@, _, !, $, %]')  // one of @ _ ! $ %
//         && matches(pw, '[A-Z]')            // an uppercase letter
//   status = valid && password == confirmPassword
//
// The Dart `matches(pw, '[@, _, !, $, %]')` is a regex character class, so its
// members are EVERY literal char between the brackets: @ , (space) _ ! $ % .
// Flutter therefore also accepts a comma or a space as the "special" character
// (e.g. `Abcdef1,` and `Abcdef1 ` are valid there). Since this feature claims
// byte-for-byte PA parity, we mirror the actual class — dropping comma/space
// would reject passwords Flutter accepts and silently disable submit here.
//
// Upper bound (Codex P2, 2026-07-17): the Identity API's `SetPasswordViewModel`
// rejects any password over 100 characters. Without a matching upper-bound rule
// here, a password-manager-generated password >100 chars reads as `valid` and
// enables submit, then SetPassword 400s — which the recovery flow currently
// surfaces as a misleading "expired link" error (the failure looks like a stale
// code, not a too-long password). MAX_LENGTH is exported so the input(s) can
// also enforce it via `maxLength` (belt-and-suspenders: the rule keeps the
// checklist honest even if a value is set programmatically past the HTML cap).

/** One rule's label + whether the candidate satisfies it. Drives the live
 *  checklist under the password field. */
export interface PasswordRule {
  key: 'length' | 'lower' | 'upper' | 'digit' | 'special' | 'maxLength';
  label: string;
  passed: boolean;
}

/** Result of {@link evaluatePassword} — per-rule state, overall strength, and
 *  the confirm-match, so a screen can both render the checklist and gate submit. */
export interface PasswordEvaluation {
  rules: PasswordRule[];
  /** All strength rules pass (Flutter `valid`). */
  valid: boolean;
  /** password === confirmPassword AND non-empty. */
  matches: boolean;
  /** Flutter `status` — valid && matches: the submit gate. */
  canSubmit: boolean;
}

// Verbatim Flutter char class `[@, _, !, $, %]` — members are @ , (space) _ ! $ %.
const SPECIAL = /[@,_!$ %]/;

/** Identity API's `SetPasswordViewModel` hard cap — see file header. Shared with
 *  the password input(s) via `maxLength={MAX_LENGTH}`. */
export const MAX_LENGTH = 100;

export function evaluatePassword(password: string, confirmPassword: string): PasswordEvaluation {
  const rules: PasswordRule[] = [
    { key: 'length', label: 'At least 8 characters', passed: password.length >= 8 },
    { key: 'lower', label: 'A lowercase letter', passed: /[a-z]/.test(password) },
    { key: 'upper', label: 'An uppercase letter', passed: /[A-Z]/.test(password) },
    { key: 'digit', label: 'A number', passed: /[0-9]/.test(password) },
    { key: 'special', label: 'A symbol (@ _ ! $ %)', passed: SPECIAL.test(password) },
    { key: 'maxLength', label: `No more than ${MAX_LENGTH} characters`, passed: password.length <= MAX_LENGTH },
  ];
  const valid = rules.every((r) => r.passed);
  const matches = password.length > 0 && password === confirmPassword;
  return { rules, valid, matches, canSubmit: valid && matches };
}
